/**
 * Project-level bulk downloads (one-pager PPTX, company profiles XLSX, contact
 * list XLSX) for a saved profiler project — the state, the fetch-and-save
 * routine, the bulk-export confirmation, the enrichment contacts dialog and the
 * outside-click handling that closes the download popup.
 *
 * Extracted from `ProfilerProjectView` (TD-2970) so the same subsystem can be
 * mounted on the Team feedback view for recipients of a feedback request. The
 * rendering lives in `components/profiler/ProjectDownload/*`; this hook owns
 * everything they read.
 *
 * Behaviour-preserving: the state keys, endpoint map, filename fallback, error
 * copy and the DOWNLOAD_LIMIT_EXCEEDED retake are the ones the project view had.
 *
 * @param {Object} params
 * @param {{ id: string, name: string }} params.project - The project being exported.
 * @param {number} params.nonExcludedResultsCount - Rows the caller sees, minus excluded ones;
 *   drives the 100 / 500 row caps and the bulk-export confirmation totals.
 * @param {boolean} params.isLoadingResults - Results still loading; every export button is
 *   disabled while true.
 * @param {boolean} [params.canExcludeCompanies=true] - Whether this viewer can exclude
 *   companies; picks the row-cap tooltip wording (false for Team feedback recipients).
 */
import { useEffect, useRef } from 'react';
import { useLocalObservable } from 'mobx-react';
import { useStore } from '../store/store';
import { API_ENDPOINTS } from '../api/endpoints';
import { HEADER_MESSAGE_TYPES } from '../utils/constants/header';
import { DOWNLOAD_LIMIT_EXCEEDED_CODE } from '../utils/constants/downloadLimits';
import { isInsideResultsMobileBar } from '../utils/resultsMobileBar';
import {
  PROFILER_DOWNLOAD_TYPES,
  saveProfilerExportFile,
  setProfilerDownloadFlags
} from '../utils/profilerDownloadHelpers';
import useSubscriptionStatus from './useSubscriptionStatus';
import useBulkExportConfirm from './useBulkExportConfirm';
import useProjectContactsDownload from './useProjectContactsDownload';

/** Stable class on the trigger's wrapper so the outside-click handler can exempt it. */
export const PROJECT_DOWNLOAD_TRIGGER_CLASS = 'project-download-trigger';

const EXPORT_ENDPOINTS = {
  onepager: API_ENDPOINTS.GET_PROFILER_PROJECT_ONE_PAGER_LIST_URL,
  contact: API_ENDPOINTS.GET_PROFILER_PROJECT_ONE_CONTACT_LIST_URL,
  profiles: API_ENDPOINTS.GET_PROFILER_PROJECT_PROFILES_LIST_URL
};

const useProjectDownloads = ({
  project,
  nonExcludedResultsCount,
  isLoadingResults,
  canExcludeCompanies = true
}) => {
  const { utilsStore, makeRequest } = useStore();

  // Bridge: the bulk-export hook is wired below the observable, but the
  // observable's onError needs to call into it (403 retake). Ref lets the
  // observable closure read the current callback without re-creating state.
  const reopenForLimitExceededRef = useRef(null);
  // Latest project, read at click time so a stale closure never exports the
  // previous project after the host re-renders with a new one.
  const projectRef = useRef(project);
  projectRef.current = project;

  // Above the observable: its export callbacks close over `refetchSubscription`
  // (stable, memoised on the store).
  const {
    downloads,
    period: contractPeriod,
    isLoading: isSubscriptionLoading,
    refetch: refetchSubscription
  } = useSubscriptionStatus();
  const isDownloadLimitReached = downloads?.limitReached ?? false;

  const state = useLocalObservable(() => ({
    isRendered: true,
    isOpen: false,
    toggle: () => {
      state.isOpen = !state.isOpen;
    },
    close: () => {
      state.isOpen = false;
    },
    isDownloadingProfilerSearchOnepagerList: false,
    isSuccessDownloadProfilerSearchOnepagerList: false,
    isDownloadingProfilerSearchContactList: false,
    isSuccessDownloadProfilerSearchContactList: false,
    isDownloadingProfilerSearchProfilesList: false,
    isSuccessDownloadProfilerSearchProfilesList: false,
    requestExport: (type = '') => {
      const endpoint = EXPORT_ENDPOINTS[type];
      const config = PROFILER_DOWNLOAD_TYPES[type];
      if (!endpoint || !config) return;

      const currentProject = projectRef.current || {};
      setProfilerDownloadFlags(state, type, { downloading: true });

      makeRequest({
        endpoint: { ...endpoint, params: { projectId: currentProject.id } },
        onSuccess: (response) => {
          // The BE records the downloads before it answers, so the balance is
          // final here. Reconcile the shared snapshot before the unmount guard:
          // it outlives this view, and every counter in the app reads it.
          refetchSubscription?.();
          if (!state.isRendered) return;
          // Never rejects: a failure is reported through the per-type flags.
          void saveProfilerExportFile({
            url: response.url,
            type,
            state,
            projectName: currentProject.name
          });
        },
        onError: (errorMessage, response) => {
          const isLimitExceeded = response?.code === DOWNLOAD_LIMIT_EXCEEDED_CODE;
          // The BE says the allowance is spent; the cached counter may still
          // show a balance.
          if (isLimitExceeded) refetchSubscription?.();
          if (!state.isRendered) return;
          setProfilerDownloadFlags(state, type, { downloading: false, success: false });

          if (isLimitExceeded) {
            // Re-open the bulk export modal seeded with the BE's rich envelope
            // so the user sees the precise shortfall instead of a generic toast.
            reopenForLimitExceededRef.current?.(type, response);
            return;
          }

          utilsStore.setHeaderMessage(
            `Failed to download ${config.failureLabel}.${errorMessage ? ' ' + errorMessage : ''}`,
            HEADER_MESSAGE_TYPES.ERROR
          );
        }
      });
    }
  }));

  useEffect(() => {
    return () => {
      state.isRendered = false;
    };
  }, [state]);

  const {
    bulkExportPendingType,
    bulkExportPrefillData,
    requestBulkExport,
    confirmBulkExport,
    cancelBulkExport,
    reopenForLimitExceeded
  } = useBulkExportConfirm({
    downloads,
    isLoading: isSubscriptionLoading,
    onDownload: (type) => state.requestExport(type)
  });
  reopenForLimitExceededRef.current = reopenForLimitExceeded;

  // Contact-list download routing (enrichment dialog vs legacy bulk export,
  // incl. the demo rule) lives in the hook. Closing the popup as the dialog
  // opens keeps the mobile sheet from covering it.
  const contactsDownload = useProjectContactsDownload({
    requestBulkExport,
    onDialogOpen: state.close
  });

  // Capture-phase listener: closes the anchored desktop popup on any click
  // outside it. The popup, its trigger and the mobile bar / sheets are exempt so
  // this handler never fights the sheet's own dismiss (TD-2769).
  useEffect(() => {
    const onGlobalClick = (event) => {
      if (!state.isOpen) return;

      const isClickInside =
        event.target.closest('.download-profiler-search-popup') ||
        event.target.closest(`.${PROJECT_DOWNLOAD_TRIGGER_CLASS}`) ||
        event.target.closest('#project-view-download-results-button-desktop') ||
        event.target.closest('.download-search-button') ||
        isInsideResultsMobileBar(event.target);
      if (isClickInside) return;

      state.close();
    };

    document.addEventListener('click', onGlobalClick, { capture: true });
    return () => {
      document.removeEventListener('click', onGlobalClick, { capture: true });
    };
  }, [state]);

  return {
    state,
    project,
    nonExcludedResultsCount,
    isLoadingResults,
    canExcludeCompanies,
    downloads,
    // TD-2813: the contract period the figures belong to, for the bulk export
    // dialog's per-period wording.
    period: contractPeriod,
    isDownloadLimitReached,
    isSubscriptionLoading,
    requestBulkExport,
    bulkExport: {
      pendingType: bulkExportPendingType,
      prefillData: bulkExportPrefillData,
      confirm: confirmBulkExport,
      cancel: cancelBulkExport
    },
    contactsDownload
  };
};

export default useProjectDownloads;
